interface ReactFiberType {
  name?: string;
  displayName?: string;
}

interface ReactFiber {
  type: ReactFiberType | ((...args: unknown[]) => unknown) | string | null;
  return: ReactFiber | null;
}

interface ReactDevToolsHook {
  renderers?: Map<number, { getCurrentFiber?: () => ReactFiber | null }>;
}

declare global {
  interface Window {
    __REACT_DEVTOOLS_GLOBAL_HOOK__?: ReactDevToolsHook;
  }
}

const EXCLUDE_PATTERNS = [
  'cubeQueryExtractor',
  'CubeQueryExtractor',
  'useQuery.ts',
  'useQueries.ts',
  'node_modules',
  'chunk-',
  'webpack',
  'react-dom',
  '@tanstack',
  'useCubeQuery',
  'QueryClientProvider',
  'QueryCache',
];

const FRAMEWORK_COMPONENTS = ['Context', 'Provider', 'Consumer', 'Suspense', 'ErrorBoundary'];

export const isLikelyMinified = (name: string): boolean => {
  const parts = name.split(' > ');
  return parts.every(part => part.length <= 3);
};

const extractFromDevTools = (): string | null => {
  if (typeof window === 'undefined' || !window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
    return null;
  }

  const hook = window.__REACT_DEVTOOLS_GLOBAL_HOOK__;
  if (!hook.renderers || hook.renderers.size === 0) {
    return null;
  }

  const renderer = Array.from(hook.renderers.values())[0];
  if (!renderer?.getCurrentFiber) {
    return null;
  }

  try {
    const fiber = renderer.getCurrentFiber();
    if (!fiber) return null;

    const componentHierarchy: string[] = [];
    let currentFiber: ReactFiber | null = fiber;

    while (currentFiber && componentHierarchy.length < 5) {
      if (currentFiber.type && typeof currentFiber.type === 'function') {
        const fiberType = currentFiber.type as ReactFiberType & ((...args: unknown[]) => unknown);
        const componentName = fiberType.name || fiberType.displayName;
        if (componentName && componentName !== 'Unknown' && componentName.length > 1) {
          const isFrameworkComponent = FRAMEWORK_COMPONENTS.some(fc => componentName.includes(fc));
          if (!isFrameworkComponent) {
            componentHierarchy.push(componentName);
          }
        }
      }
      currentFiber = currentFiber.return;
    }

    if (componentHierarchy.length > 0) {
      return componentHierarchy.length > 1
        ? componentHierarchy.slice(0, 3).reverse().join(' > ')
        : componentHierarchy[0];
    }
  } catch {}

  return null;
};

const extractFromStackTrace = (): string => {
  const stack = new Error().stack || '';
  const lines = stack.split('\n');
  const componentHierarchy: string[] = [];

  for (const line of lines) {
    if (EXCLUDE_PATTERNS.some(pattern => line.includes(pattern))) {
      continue;
    }

    const componentFileMatch = line.match(/\/([A-Z][a-zA-Z0-9]+)\.(tsx|jsx):/);
    if (componentFileMatch && !componentHierarchy.includes(componentFileMatch[1])) {
      componentHierarchy.push(componentFileMatch[1]);
    }

    const hookFileMatch = line.match(/\/(use[A-Z][a-zA-Z0-9]*)\.(ts|tsx):/);
    if (hookFileMatch && !componentHierarchy.includes(hookFileMatch[1])) {
      componentHierarchy.push(hookFileMatch[1]);
    }

    const atComponentMatch = line.match(/at\s+([A-Z][a-zA-Z0-9]+)\s+/);
    if (
      atComponentMatch &&
      atComponentMatch[1] !== 'Object' &&
      atComponentMatch[1] !== 'eval' &&
      !componentHierarchy.includes(atComponentMatch[1])
    ) {
      componentHierarchy.push(atComponentMatch[1]);
    }

    const atHookMatch = line.match(/at\s+(use[A-Z][a-zA-Z0-9]+)\s+/);
    if (atHookMatch && !componentHierarchy.includes(atHookMatch[1])) {
      componentHierarchy.push(atHookMatch[1]);
    }

    if (componentHierarchy.length >= 3) {
      break;
    }
  }

  if (componentHierarchy.length > 0) {
    return componentHierarchy.length > 1 ? componentHierarchy.slice(0, 3).join(' > ') : componentHierarchy[0];
  }

  for (const line of lines) {
    const pathMatch = line.match(/\/([A-Za-z][A-Za-z0-9]*)\.(tsx?|jsx?):/);
    if (pathMatch && !EXCLUDE_PATTERNS.some(pattern => line.includes(pattern))) {
      const componentName = pathMatch[1];
      if (componentName.charAt(0) === componentName.charAt(0).toUpperCase() || componentName.startsWith('use')) {
        return componentName;
      }
    }
  }

  return 'UnknownComponent';
};

export const extractComponentName = (): string => {
  try {
    const devToolsResult = extractFromDevTools();
    if (devToolsResult) {
      return devToolsResult;
    }

    return extractFromStackTrace();
  } catch {
    return 'UnknownComponent';
  }
};

const kebabToPascalCase = (str: string): string => {
  return str
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
};

export const deriveContextFromQueryKey = (queryKey: unknown[]): string | null => {
  const stringKeys = queryKey.filter((key): key is string => typeof key === 'string');
  if (stringKeys.length === 0) return null;

  const parts = stringKeys.slice(0, 2).map(key => kebabToPascalCase(key.replace(/^(get-|use-)/i, '')));
  return parts.join(' > ');
};

interface CubeQueryBasic {
  measures?: string[];
  dimensions?: string[];
}

export const deriveContextFromQuery = (query: CubeQueryBasic): string | null => {
  const sources = [...(query.measures || []), ...(query.dimensions || [])];
  if (sources.length === 0) return null;

  const firstSource = sources[0];
  const [cubeName, memberName] = firstSource.split('.');
  if (!cubeName) return null;

  return memberName ? `${cubeName}.${memberName}` : cubeName;
};

export const getPageNameFromPath = (path: string): string => {
  if (!path || path === '/') return 'Home';

  const pathParts = path.split('/').filter(Boolean);

  if (pathParts.length === 0) return 'Home';

  return pathParts
    .map(part =>
      part
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
    )
    .join(' / ');
};

export const extractComponentNameWithFallback = (query?: CubeQueryBasic, queryKey?: unknown[]): string => {
  try {
    const devToolsResult = extractFromDevTools();
    if (devToolsResult && !isLikelyMinified(devToolsResult)) return devToolsResult;

    const stackResult = extractFromStackTrace();
    if (stackResult !== 'UnknownComponent' && !isLikelyMinified(stackResult)) return stackResult;

    if (queryKey && queryKey.length > 0) {
      const keyContext = deriveContextFromQueryKey(queryKey);
      if (keyContext) return keyContext;
    }

    if (query) {
      const cubeContext = deriveContextFromQuery(query);
      if (cubeContext) return cubeContext;
    }

    if (typeof window !== 'undefined') {
      const pageName = getPageNameFromPath(window.location.pathname);
      if (pageName && pageName !== 'Home') return `${pageName} (Query)`;
    }

    return 'UnknownComponent';
  } catch {
    return 'UnknownComponent';
  }
};
